import type { AxiosHeaders } from 'axios';
import { api } from './index';
import type { PatientScheduleBlock, ShiftScheduleBlockFlag } from './patient-schedule-blocks';
import { type Attendance } from './attendances';
import { type Caregiver } from './caregivers';
import { type Patient } from './patients';
import { careerColors } from '../utils/careers';
import type { ClinicalAuthor } from '../utils/clinical-author';
import type { ClinicalVoidable } from '../utils/clinical-void';

const BASE_PATH = '/api/internal/shifts';
interface ReportImage extends ClinicalVoidable {
  id: number;
  imageUrlLarge: string;
  humanCreatedAt: string;
  formattedText: string;
  author: ClinicalAuthor | null;
}

interface ReportText extends ClinicalVoidable {
  id: number;
  text: string;
  humanCreatedAt: string;
  formattedText: string;
  edited: boolean;
  author: ClinicalAuthor | null;
}

export type Shift = {
  id: number;
  patientId: number;
  patientFullName: string;
  patientAddress: string;
  caregiverId: number;
  caregiverFullName: string;
  caregiverCareer: keyof typeof careerColors;
  caregiverHumanCareer: string;
  startDatetime: string;
  startTime: string;
  endDatetime: string;
  endTime: string;
  caregiverCost: number;
  price: number;
  caregiverCostPerHour: number;
  pricePerHour: number;
  humanDate: string;
  humanTime: string;
  humanLetter: string;
  isWeekend: boolean;
  withTaxBill: boolean;
  humanTaxBill: string;
  hours: number;
  status: { name: string, color: string };
  suspended: boolean;
  canSuspend: boolean;
  checkIn: Attendance | null;
  checkOut: Attendance | null;
  reportImages: Array<ReportImage>;
  reportImagesSummary: string;
  hasReports: boolean;
  hasIncidents: boolean;
  canCheckInByAdmin: boolean;
  canCheckOutByAdmin: boolean;
  canReplaceCaregiver: boolean;
  reportTexts: Array<ReportText>;
  canEditCheckIn: boolean;
  canEditCheckOut: boolean;
  canEditAttendances: boolean;
  caregiverContractedWithSalary?: boolean;
  invoiced?: boolean;
}

export type ShiftWithoutFinancialInformation = Omit<
  Shift,
  'price' | 'caregiverCost' | 'pricePerHour' | 'caregiverCostPerHour' | 'humanTaxBill'
>;

// The index leaves out the reports and their summary: lists carry no clinical
// data, which comes with one shift's show.
type ShiftReportFields = 'reportImages' | 'reportTexts' | 'reportImagesSummary';
export type ShiftListItem =
  Omit<Shift, ShiftReportFields> | Omit<ShiftWithoutFinancialInformation, ShiftReportFields>;

export interface CustomShiftRange {
  startTime: string;
  endTime: string;
}

// One day of one caregiver in a batch. customShift selects the batch-wide
// start/end pair (the "24 horas" type); customShifts lists the per-range slots
// of the "Personalizados" type, one shift each.
export interface CaregiverShift {
  day: string;
  dayShift: boolean;
  nightShift: boolean;
  customShift: boolean;
  customShifts?: Array<CustomShiftRange>;
}

// One caregiver's pricing inside a batch. The backend resolves each caregiver
// from its own entry first, then the flat fields below, then the caregiver's own
// PricingStructure — so 0 here is an explicit, honored value.
export interface CaregiverPricing {
  caregiverId: number;
  caregiverWeekShiftCost: number;
  caregiverWeekendShiftCost: number;
  weekShiftPrice: number;
  weekendShiftPrice: number;
  caregiverWeekShiftCostPerHour: number;
  caregiverWeekendShiftCostPerHour: number;
  weekShiftPricePerHour: number;
  weekendShiftPricePerHour: number;
  withTaxBill: boolean;
}

export interface ShiftBatchCreateForm {
  id?: number;
  patientId: number;
  caregiversIds: Array<number>;
  caregiversShifts: Array<Array<CaregiverShift>>;
  // Flat, batch-wide pricing: still sent by the single-shift form through
  // transformShiftToShiftBatchCreateForm. The batch form sends caregiversPricing instead.
  caregiverWeekShiftCost?: number;
  caregiverWeekendShiftCost?: number;
  weekShiftPrice?: number;
  weekendShiftPrice?: number;
  caregiverWeekShiftCostPerHour?: number;
  caregiverWeekendShiftCostPerHour?: number;
  weekShiftPricePerHour?: number;
  weekendShiftPricePerHour?: number;
  withTaxBill?: boolean;
  caregiversPricing?: Array<CaregiverPricing>;
  startDate: string;
  // Batch-wide custom pair. The "Personalizados" type sends none: its ranges
  // travel per day inside caregiversShifts.
  startTime?: string;
  endTime?: string;
}

export interface ShiftRecurrentCreateForm {
  patientId: number;
  caregiverId: number;
  caregiverWeekShiftCost: number;
  caregiverWeekendShiftCost: number;
  weekShiftPrice: number;
  weekendShiftPrice: number;
  caregiverWeekShiftCostPerHour: number;
  caregiverWeekendShiftCostPerHour: number;
  weekShiftPricePerHour: number;
  weekendShiftPricePerHour: number;
  withTaxBill: boolean;
  shiftType: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  recurrenceType: string;
  recurrenceInterval: number;
  recurrenceDays?: Array<string>;
  recurrent: boolean;
}

export interface ShiftForm {
  id?: number;
  patientId: number;
  caregiverId: number;
  caregiverCost: number;
  price: number;
  caregiverCostPerHour: number;
  pricePerHour: number;
  withTaxBill: boolean;
  shiftType: string;
  startTime: string;
  endTime: string;
  date?: string;
  suspended?: boolean;
}

export interface ShiftBatchUpdateForm {
  patientId: number;
  oldCaregiverId: number;
  newCaregiverId: number;
  startDate: string;
  endDate: string;
}

export interface ShiftCalendarHeader {
  date: string;
  title: string;
  headerProps: { class: string; holiday: boolean };
  children: Array<{ value: string; cellProps?: { class: string } }>;
}

export interface InstanceShifts {
  caregiver: Caregiver;
  patient: Patient;
  [key: string]: Caregiver | Patient | Shift[] | unknown;
}
export interface ShiftsCalendarSummary {
  price: number;
  cost: number;
  totalShifts: number;
  totalPrice: number;
  totalCost: number;
  totalProfit: number;
  margin: number;
}
export interface ShiftsCalendarTotalSummary {
  totalShifts: number;
  totalPrice: number;
  totalCost: number;
  totalProfit: number;
  margin: number;
  salaryBasedCost?: number;
  perShiftCost?: number;
}

export interface ShiftsCalendar {
  shifts: Array<InstanceShifts>;
  headers: Array<ShiftCalendarHeader>;
  dateTitle: string;
  groupByClass: string;
  summary: ShiftsCalendarSummary[];
  totalSummary: ShiftsCalendarTotalSummary;
  // Patient schedule blocks overlapping the range (care-planning companies only).
  scheduleBlocks?: Array<PatientScheduleBlock>;
}

export interface PdfShiftsCalendar {
  headers: AxiosHeaders;
  data: Blob;
}

export interface XlsxShiftsCalendar {
  headers: AxiosHeaders;
  data: Blob;
}

export type ShiftTodayListItem = Pick<
  Shift,
  'id' | 'patientId' | 'patientFullName' | 'caregiverId' | 'caregiverFullName' |
  'caregiverCareer' | 'caregiverHumanCareer' | 'humanTime' | 'status' |
  'checkIn' | 'checkOut' | 'hasReports' | 'hasIncidents' |
  'canCheckInByAdmin' | 'canCheckOutByAdmin'
> & {
  // its end time has passed: marking it records start and end in one step
  hasEnded: boolean;
  // pinned shift address, else the patient's primary address; null when unknown
  latitude: number | null;
  longitude: number | null;
};

export type ShiftCalendarCell = Pick<
  Shift,
  'id' | 'startDatetime' | 'endDatetime' | 'humanLetter' | 'humanTime' | 'status' |
  'hasReports' | 'hasIncidents'
> & Partial<Pick<Shift, 'price' | 'caregiverCost' | 'humanTaxBill' | 'caregiverContractedWithSalary'>>
  & { scheduleBlock?: ShiftScheduleBlockFlag };

export const shiftsApi = {
  show(id: number): Promise<Shift | ShiftWithoutFinancialInformation> {
    return api({
      method: 'get',
      url: `${BASE_PATH}/${id}`,
    }).then(response => response.data.shift);
  },
  index(queryParams: string): Promise<Array<ShiftListItem>> {
    return api({
      method: 'get',
      url: `${BASE_PATH}?${queryParams}`,
    }).then(response => response.data.shifts);
  },
  indexTodayList(queryParams: string): Promise<Array<ShiftTodayListItem>> {
    return api({
      method: 'get',
      url: `${BASE_PATH}?${queryParams}`,
    }).then(response => response.data.shifts);
  },
  indexAsCalendar(queryParams: string): Promise<ShiftsCalendar> {
    return api({
      method: 'get',
      url: `/api/internal/shifts_calendar?${queryParams}`,
    }).then(response => response.data);
  },
  indexAsCalendarPdf(queryParams: string, includeRequirements = false): Promise<PdfShiftsCalendar> {
    const requirementsParam = includeRequirements ? '&include_requirements=true' : '';

    return api({
      method: 'get',
      url: `/api/internal/shifts_calendar.pdf?${queryParams}${requirementsParam}`,
      responseType: 'blob',
    });
  },
  indexAsCalendarXlsx(queryParams: string): Promise<XlsxShiftsCalendar> {
    return api({
      method: 'get',
      url: `/api/internal/shifts_calendar.xlsx?${queryParams}`,
      responseType: 'blob',
    });
  },
  create(
    shiftsFormData: ShiftBatchCreateForm | ShiftRecurrentCreateForm,
  ): Promise<Array<Shift>> {
    return api({
      method: 'post',
      url: BASE_PATH,
      data: { shifts: shiftsFormData },
    }).then(response => response.data);
  },
  update(shiftForm: Partial<ShiftForm>): Promise<Shift> {
    return api({
      method: 'put',
      url: `${BASE_PATH}/${shiftForm.id}`,
      data: { shift: shiftForm },
    }).then(response => response.data.shift);
  },
  batchUpdate(shiftBatchUpdateForm: ShiftBatchUpdateForm): Promise<Array<Shift>> {
    return api({
      method: 'put',
      url: BASE_PATH,
      data: shiftBatchUpdateForm,
    }).then(response => response.data);
  },
  destroy(id: number | undefined, queryParams: string | undefined): Promise<void> {
    let url;
    if (id) {
      url = `${BASE_PATH}/${id}`;
    } else if (queryParams) {
      url = `${BASE_PATH}?${queryParams}`;
    }

    return api({
      method: 'delete',
      url,
    }).then(() => {});
  },
};
